import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Lock, ChevronRight, Brain, Zap, Smile, Calculator, BookOpen, Puzzle, Target, Grid, Scissors, Palette, MousePointer2, Heart, Wind } from 'lucide-react'; 
import { useAppData } from '../hooks/useAppData';

import WhackAMoleGame from '../components/games/WhackAMoleGame';
import MemoryGame from '../components/games/MemoryGame';
import BreathingExercise from '../components/games/BreathingExercise';
import BodyFocusedRelaxation from '../components/games/BodyFocusedRelaxation';
import JournalGame from '../components/games/JournalGame';
import TicTacToeGame from '../components/games/TicTacToeGame';
import SimonGame from '../components/games/SimonGame';
import RockPaperScissorsGame from '../components/games/RockPaperScissorsGame';
import WordleGame from '../components/games/WordleGame';
import ColorMatchGame from '../components/games/ColorMatchGame';
import EmojiMatchGame from '../components/games/EmojiMatchGame';
import PuzzleSlideGame from '../components/games/PuzzleSlideGame';
import MathGame from '../components/games/MathGame';
import CatchItemsGame from '../components/games/CatchItemsGame';
import RunerGame from '../components/games/RunerGame';
import Glyph from '../components/glyphs/Glyph';

interface Game {
  id: string;
  name: string;
  desc: string;
  icon: React.ElementType;
  color: string;
  component: React.FC<{ onComplete: (score: number) => void }>;
  minLevel: number;
  category: 'cbt' | 'casual';
}

// תרגילי CBT - צבעים רגועים (כחול/סגול/ירוק עדין)
const CBT_EXERCISES: Game[] = [
  { 
    id: 'breathing', 
    name: 'תרגיל נשימות', 
    desc: 'הרגעה ושליטה בחרדה', 
    icon: Wind, 
    color: 'bg-blue-500/5 border-blue-400/20 hover:border-blue-400/40', 
    component: BreathingExercise, 
    minLevel: 1,
    category: 'cbt'
  },
  { 
    id: 'pmr', 
    name: 'הרפיית שרירים', 
    desc: 'שחרור מתח גופני', 
    icon: Heart, 
    color: 'bg-purple-500/5 border-purple-400/20 hover:border-purple-400/40', 
    component: BodyFocusedRelaxation, 
    minLevel: 1,
    category: 'cbt'
  },
  { 
    id: 'journal', 
    name: 'יומן מחשבות', 
    desc: 'רישום ועיבוד רגשות', 
    icon: BookOpen, 
    color: 'bg-indigo-500/5 border-indigo-400/20 hover:border-indigo-400/40', 
    component: JournalGame, 
    minLevel: 1,
    category: 'cbt'
  },
  { 
    id: 'emoji', 
    name: 'זיהוי רגשות', 
    desc: 'חיבור למצב הרגשי', 
    icon: Smile, 
    color: 'bg-teal-500/5 border-teal-400/20 hover:border-teal-400/40', 
    component: EmojiMatchGame, 
    minLevel: 2,
    category: 'cbt'
  },
];

// משחקים לפנאי - צבעים עדינים (ירוק/תכלת/כתום)
const CASUAL_GAMES: Game[] = [
  { 
    id: 'memory', 
    name: 'משחק זיכרון', 
    desc: 'חידוד המוח', 
    icon: Brain, 
    color: 'bg-emerald-500/5 border-emerald-400/20 hover:border-emerald-400/40', 
    component: MemoryGame, 
    minLevel: 1,
    category: 'casual'
  },
  { 
    id: 'simon', 
    name: 'סיימון אומר', 
    desc: 'רצף צבעים', 
    icon: Play, 
    color: 'bg-cyan-500/5 border-cyan-400/20 hover:border-cyan-400/40', 
    component: SimonGame, 
    minLevel: 1,
    category: 'casual'
  },
  { 
    id: 'tictac', 
    name: 'איקס עיגול', 
    desc: 'אסטרטגיה נגד המחשב', 
    icon: Grid, 
    color: 'bg-sky-500/5 border-sky-400/20 hover:border-sky-400/40', 
    component: TicTacToeGame, 
    minLevel: 1,
    category: 'casual'
  },
  { 
    id: 'wordle', 
    name: 'ניחוש מילים', 
    desc: 'אתגר שפה ולוגיקה', 
    icon: Brain, 
    color: 'bg-lime-500/5 border-lime-400/20 hover:border-lime-400/40', 
    component: WordleGame, 
    minLevel: 2,
    category: 'casual'
  },
  { 
    id: 'math', 
    name: 'חשבון מהיר', 
    desc: 'תרגול מתמטיקה', 
    icon: Calculator, 
    color: 'bg-green-500/5 border-green-400/20 hover:border-green-400/40', 
    component: MathGame, 
    minLevel: 2,
    category: 'casual'
  },
  { 
    id: 'puzzle', 
    name: 'פאזל מספרים', 
    desc: 'סדר והיגיון', 
    icon: Puzzle, 
    color: 'bg-teal-500/5 border-teal-400/20 hover:border-teal-400/40', 
    component: PuzzleSlideGame, 
    minLevel: 3,
    category: 'casual'
  },
  { 
    id: 'whack', 
    name: 'פגע בחפרפרת', 
    desc: 'מהירות תגובה', 
    icon: Target, 
    color: 'bg-orange-500/5 border-orange-400/20 hover:border-orange-400/40', 
    component: WhackAMoleGame, 
    minLevel: 3,
    category: 'casual'
  },
  { 
    id: 'catch', 
    name: 'תפוס כוכבים', 
    desc: 'תיאום עין-יד', 
    icon: MousePointer2, 
    color: 'bg-amber-500/5 border-amber-400/20 hover:border-amber-400/40', 
    component: CatchItemsGame, 
    minLevel: 3,
    category: 'casual'
  },
  { 
    id: 'runer', 
    name: 'רץ וקפוץ', 
    desc: 'הימנע ממכשולים', 
    icon: Zap, 
    color: 'bg-yellow-500/5 border-yellow-400/20 hover:border-yellow-400/40', 
    component: RunerGame, 
    minLevel: 4,
    category: 'casual'
  },
  { 
    id: 'rps', 
    name: 'אבן נייר ומספריים', 
    desc: 'משחק קלאסי', 
    icon: Scissors, 
    color: 'bg-rose-500/5 border-rose-400/20 hover:border-rose-400/40', 
    component: RockPaperScissorsGame, 
    minLevel: 4,
    category: 'casual'
  },
  { 
    id: 'color', 
    name: 'התאמת צבעים', 
    desc: 'אתגר ויזואלי', 
    icon: Palette, 
    color: 'bg-pink-500/5 border-pink-400/20 hover:border-pink-400/40', 
    component: ColorMatchGame, 
    minLevel: 5,
    category: 'casual'
  },
];

const ALL_GAMES = [...CBT_EXERCISES, ...CASUAL_GAMES];

export default function MiniGames() {
  const { completeMission } = useAppData();
  const [activeGame, setActiveGame] = useState<string | null>(null);
  const [rewardModal, setRewardModal] = useState<{amount: number} | null>(null);
  
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const savedScrollPos = useRef(0);

  const setListRef = useCallback((node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;
      if (node) {
          node.scrollTop = savedScrollPos.current;
      }
  }, []);

  const handleGameComplete = async (score: number) => {
    if (activeGame) {
      const reward = Math.floor(score / 10);
      
      if (reward > 0) {
        await completeMission('game_played', reward); 
      }
      setRewardModal({ amount: reward });
    }
  };

  const handleCloseModal = () => {
    setRewardModal(null);
    setActiveGame(null);
  };

  const ActiveComponent = ALL_GAMES.find(g => g.id === activeGame)?.component;

  const renderGameCard = (game: Game) => {
    const isLocked = false; // (profile?.level || 1) < game.minLevel;

    return (
      <motion.div 
        key={game.id}
        whileTap={!isLocked ? { scale: 0.98 } : {}}
        onClick={() => {
          if (!isLocked) {
            if (scrollContainerRef.current) {
              savedScrollPos.current = scrollContainerRef.current.scrollTop;
            }
            setActiveGame(game.id);
          }
        }}
        className={`group relative overflow-hidden rounded-2xl p-5 border-2 transition-all min-h-[90px] flex items-center ${
          isLocked 
            ? 'bg-muted/30 border-border/50 cursor-not-allowed opacity-50' 
            : `${game.color} cursor-pointer shadow-sm hover:shadow-md`
        }`}
      >
        <div className="relative z-10 flex justify-between items-center w-full">
          <div className="flex-1">
            <h3 className={`text-lg font-bold mb-0.5 ${isLocked ? 'text-muted-foreground' : 'text-foreground'}`}>
              {game.name}
            </h3>
            <p className={`text-xs ${isLocked ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
              {isLocked ? `נפתח ברמה ${game.minLevel}` : game.desc}
            </p>
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ml-3 ${
            isLocked 
              ? 'bg-muted/50' 
              : 'bg-background/50 backdrop-blur-sm group-hover:bg-background/70'
          }`}>
            {isLocked ? (
              <Lock className="text-muted-foreground" size={20} />
            ) : (
              <game.icon size={24} className="text-foreground/80" />
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="h-full bg-background text-foreground flex flex-col relative overflow-hidden dir-rtl transition-colors duration-300">
      
      {/* Header */}
      <div className="p-6 pb-3 z-10 bg-gradient-to-b from-background via-background/95 to-transparent">
        {activeGame ? (
          <button 
            onClick={() => setActiveGame(null)} 
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <ChevronRight size={20} /> חזרה למשחקים
          </button>
        ) : (
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1 flex items-center gap-2">
              משחקייה <Glyph name="game" size={34} />
            </h1>
            <p className="text-muted-foreground text-sm">שחק, תהנה וצבור יהלומים!</p>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden relative">
        <AnimatePresence mode="wait">
          {activeGame && ActiveComponent ? (
            <motion.div 
              key="game"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="h-full p-4 overflow-y-auto"
            >
              <ActiveComponent onComplete={handleGameComplete} />
            </motion.div>
          ) : (
            <div 
              ref={setListRef}
              className="overflow-y-auto pb-24 px-6 scrollbar-hide h-full content-start pt-2 space-y-8"
            >
              {/* תרגילים */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <Brain size={20} className="text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-foreground">תרגילי CBT</h2>
                    <p className="text-xs text-muted-foreground">כלים להרגעה וויסות רגשי</p>
                  </div>
                </div>
                <div className="grid gap-3">
                  {CBT_EXERCISES.map(renderGameCard)}
                </div>
              </div>

              {/* משחקים לפנאי */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <Zap size={20} className="text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-foreground">משחקים לפנאי</h2>
                    <p className="text-xs text-muted-foreground">כיף, אתגר וחידוד המוח</p>
                  </div>
                </div>
                <div className="grid gap-3">
                  {CASUAL_GAMES.map(renderGameCard)}
                </div>
              </div>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Reward Modal */}
      <AnimatePresence>
        {rewardModal && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="bg-card border border-border p-8 rounded-3xl text-center shadow-2xl max-w-sm w-full"
            >
              <div className="mb-4 animate-bounce flex justify-center"><Glyph name="diamond" size={64} /></div>
              <h2 className="text-2xl font-bold text-foreground mb-2">כל הכבוד!</h2>
              <p className="text-muted-foreground mb-6">
                הרווחת <span className="text-primary font-bold text-xl">{rewardModal.amount}</span> יהלומים!
              </p>
              <button
                onClick={handleCloseModal}
                className="bg-primary hover:bg-primary/90 text-white font-bold py-3 px-8 rounded-xl w-full transition-colors"
              >
                איזה כיף!
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}